import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  BookUser,
  Building,
  Building2,
  Check,
  ChevronDown,
  Inbox,
  Landmark,
  Languages,
  ListTodo,
  LogOut,
  Monitor,
  Moon,
  Plus,
  ReceiptText,
  Search,
  Sun,
} from 'lucide-react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import {
  Button as AriaButton,
  Header,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Separator,
} from 'react-aria-components';
import { meQuery } from '../../api/queries';
import type { OrganizationSummary } from '../../api/types';
import { useI18n } from '../../i18n/I18nProvider';
import { UserText } from '../../ui/Bidi';
import { useAccess } from '../access';
import { useSignOut } from '../session';
import { useTheme, type ThemePreference } from '../theme';
import { CommandPaletteProvider, usePalette } from './CommandPalette';
import { HUBS, isModuleVisible, isParentPage, type HubId, type HubModule } from './hubs';
import { TestEnvStrip } from './TestEnvStrip';
import { ProductMark } from './ProductMark';
import styles from './Shell.module.css';

/**
 * Application frame (docs/design/IA.md): a top bar with the product mark, the organization switcher
 * (the company is a context, not a section), the job hubs that exist, the command palette, language
 * and account, above the page content. No side rail, no programs drawer, no shortcuts popover.
 */
export function AppShell({ orgId, children }: { orgId?: string; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <CommandPaletteProvider orgId={orgId}>
      <a href="#main" className={styles.skipLink}>
        {t.app.skipToContent}
      </a>
      <TopBar orgId={orgId} />
      <main id="main" tabIndex={-1} className={styles.main}>
        {children}
      </main>
    </CommandPaletteProvider>
  );
}

/**
 * Navigation (IA.md; council UX decision 2026-10-04): "Today" and one link per job hub in the top
 * bar; the pages of the current hub form a strip under it. Only modules in `USER_VISIBLE_MODULES`
 * (hubs.ts) are shown — a module whose page is not complete is hidden, not teased with a "not
 * built yet" page. Pages are shown only to roles that hold their permission.
 */
const HUB_ICONS: Record<HubId, ReactNode> = {
  billing: <ReceiptText size={17} />,
  incoming: <Inbox size={17} />,
  filing: <Landmark size={17} />,
  records: <BookUser size={17} />,
  organization: <Building size={17} />,
};

/**
 * The sticky header's height as `--app-header-size` on the root element, so sticky regions of a
 * page (the register's find panel, the editor's summary rail) sit right under it.
 */
function useHeaderSize() {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const header = ref.current;
    if (!header) return undefined;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty('--app-header-size', `${header.getBoundingClientRect().height}px`);
    publish();
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function TopBar({ orgId }: { orgId?: string | undefined }) {
  const { t } = useI18n();
  const access = useAccess(orgId);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const headerRef = useHeaderSize();

  const hubs = orgId
    ? HUBS.map((hub) => ({
        ...hub,
        modules: hub.modules.filter(
          (m) => isModuleVisible(m.id) && m.permissions.some((p) => access.can(p)),
        ),
      })).filter((hub) => hub.modules.length > 0)
    : [];
  const pathOf = (module: HubModule) => module.route.replace('$orgId', orgId ?? '');
  const activeHub = hubs.find((hub) =>
    hub.modules.some((m) => pathname === pathOf(m) || pathname.startsWith(`${pathOf(m)}/`)),
  );

  const hubLinks = (place: 'bar' | 'strip') =>
    orgId ? (
      <>
        <Link
          to="/orgs/$orgId"
          params={{ orgId }}
          className={styles.navLink}
          activeOptions={{ exact: true }}
          activeProps={{ 'aria-current': 'page' }}
          data-testid={place === 'bar' ? 'hub-today' : 'hub-today-strip'}
        >
          <span aria-hidden="true" className={styles.hubIcon}>
            <ListTodo size={17} />
          </span>
          <span className={styles.hubLabel}>{t.nav.today}</span>
        </Link>
        {hubs.map((hub) => {
          const first = hub.modules[0];
          if (!first) return null;
          return (
            <Link
              key={hub.id}
              to={first.route}
              params={{ orgId }}
              className={styles.navLink}
              aria-current={activeHub?.id === hub.id ? 'page' : undefined}
              data-testid={place === 'bar' ? `hub-${hub.id}` : `hub-${hub.id}-strip`}
            >
              <span aria-hidden="true" className={styles.hubIcon}>
                {HUB_ICONS[hub.id]}
              </span>
              <span className={styles.hubLabel}>{t.nav[hub.id]}</span>
            </Link>
          );
        })}
      </>
    ) : null;

  return (
    <header className={styles.topbar} ref={headerRef}>
      <TestEnvStrip />
      <div className={styles.topbarRow}>
        {orgId ? (
          <Link
            to="/orgs/$orgId"
            params={{ orgId }}
            className={styles.brand}
            aria-label={t.app.homeLabel}
          >
            <ProductMark />
            <span className={styles.brandName}>{t.app.name}</span>
          </Link>
        ) : (
          <Link to="/" className={styles.brand} aria-label={t.app.homeLabel}>
            <ProductMark />
            <span className={styles.brandName}>{t.app.name}</span>
          </Link>
        )}
        <OrgSwitcher orgId={orgId} />
        {orgId ? (
          <nav className={styles.nav} aria-label={t.nav.label} data-testid="hub-nav">
            {hubLinks('bar')}
          </nav>
        ) : null}
        <span className={styles.spacer} />
        <PaletteButton />
        <LanguageButton />
        <UserMenu />
      </div>
      {orgId ? (
        <nav className={styles.navStrip} aria-label={t.nav.label}>
          {hubLinks('strip')}
        </nav>
      ) : null}
      {orgId && activeHub && activeHub.modules.length > 1 ? (
        <nav
          className={styles.pageStrip}
          aria-label={t.nav.hubPages(t.nav[activeHub.id])}
          data-testid="page-strip"
        >
          {activeHub.modules.map((module) => (
            <Link
              key={module.id}
              to={module.route}
              params={{ orgId }}
              className={styles.pageLink}
              activeOptions={{ exact: isParentPage(module, activeHub.modules) }}
              activeProps={{ 'aria-current': 'page' }}
              data-testid={`page-${module.id}`}
            >
              {t.modules[module.id].name}
            </Link>
          ))}
        </nav>
      ) : null}
    </header>
  );
}

function envLabel(t: ReturnType<typeof useI18n>['t'], env: OrganizationSummary['etaEnvironment']) {
  return t.env[env];
}

function OrgSwitcher({ orgId }: { orgId?: string | undefined }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data: me } = useQuery(meQuery);
  const organizations = me?.organizations ?? [];
  const current = organizations.find((org) => org.id === orgId);
  return (
    <MenuTrigger>
      <AriaButton
        className={styles.orgPill}
        aria-label={t.shell.orgSwitcherLabel(current?.name ?? null)}
      >
        <Building2 size={16} aria-hidden="true" />
        <span className={styles.orgName}>
          {current ? <UserText>{current.name}</UserText> : t.shell.chooseOrganization}
        </span>
        {current ? (
          <span className={styles.envTag} data-env={current.etaEnvironment}>
            {envLabel(t, current.etaEnvironment)}
          </span>
        ) : null}
        <ChevronDown size={14} aria-hidden="true" />
      </AriaButton>
      <Popover className={styles.menuPopover} placement="bottom start" offset={6}>
        <Menu
          className={styles.menu}
          aria-label={t.shell.organizations}
          onAction={(key) => {
            const id = String(key);
            if (id === '__all') void navigate({ to: '/orgs' });
            else if (id === '__new') void navigate({ to: '/orgs/new' });
            else void navigate({ to: '/orgs/$orgId', params: { orgId: id } });
          }}
        >
          <MenuSection>
            <Header className={styles.menuHeader}>{t.shell.organizations}</Header>
            {organizations.map((org) => (
              <MenuItem key={org.id} id={org.id} textValue={org.name} className={styles.menuItem}>
                <span className={styles.menuItemText}>
                  <span className={styles.menuItemTitle}>
                    <UserText>{org.name}</UserText>
                  </span>
                  <span className={styles.menuItemMeta}>
                    {envLabel(t, org.etaEnvironment)} ·{' '}
                    {t.roles[org.role as keyof typeof t.roles] ?? org.role}
                  </span>
                </span>
                {org.id === orgId ? (
                  <Check size={16} className={styles.menuCheck} aria-label={t.shell.current} />
                ) : null}
              </MenuItem>
            ))}
          </MenuSection>
          <Separator className={styles.menuSeparator} />
          <MenuItem id="__all" textValue={t.shell.allOrganizations} className={styles.menuItem}>
            <Building2 size={16} aria-hidden="true" />
            {t.shell.allOrganizations}
          </MenuItem>
          <MenuItem id="__new" textValue={t.shell.createOrganization} className={styles.menuItem}>
            <Plus size={16} aria-hidden="true" />
            {t.shell.createOrganization}
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

function PaletteButton() {
  const { t } = useI18n();
  const palette = usePalette();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <AriaButton
      className={`${styles.toolButton} ${styles.searchButton}`}
      data-testid="palette-open"
      onPress={palette.open}
      aria-label={t.palette.open}
      aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
    >
      <Search size={16} aria-hidden="true" />
      <span className={styles.searchLabel}>{t.palette.open}</span>
      <kbd className={styles.kbd} aria-hidden="true">
        {isMac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </AriaButton>
  );
}

function LanguageButton() {
  const { t, locale, setLocale } = useI18n();
  const next = locale === 'ar' ? 'en' : 'ar';
  return (
    <AriaButton
      className={styles.toolButton}
      onPress={() => setLocale(next)}
      aria-label={`${t.shell.switchLanguage} (${t.shell.otherLanguageName})`}
    >
      <Languages size={16} aria-hidden="true" />
      <span className={styles.langLabel} lang={next}>
        {t.shell.otherLanguageName}
      </span>
    </AriaButton>
  );
}

const THEME_ICONS: Record<ThemePreference, ReactNode> = {
  system: <Monitor size={16} aria-hidden="true" />,
  light: <Sun size={16} aria-hidden="true" />,
  dark: <Moon size={16} aria-hidden="true" />,
};

function UserMenu() {
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();
  const { data: me } = useQuery(meQuery);
  const signOut = useSignOut();
  const name = me?.user.displayName ?? '';
  const initial = Array.from(name.trim())[0]?.toUpperCase() ?? '?';
  return (
    <MenuTrigger>
      <AriaButton className={styles.toolButton} aria-label={t.shell.accountMenu}>
        <span className={styles.avatar} aria-hidden="true">
          {initial}
        </span>
      </AriaButton>
      <Popover className={styles.menuPopover} placement="bottom end" offset={6}>
        <div className={styles.identity}>
          <span className={styles.identityName}>
            <UserText>{name}</UserText>
          </span>
          <span className={styles.identityEmail}>{me?.user.email}</span>
        </div>
        <Menu
          className={styles.menu}
          aria-label={t.shell.accountMenu}
          onAction={(key) => {
            const id = String(key);
            if (id === 'sign-out') signOut.mutate();
            else if (id === 'system' || id === 'light' || id === 'dark') setTheme(id);
          }}
        >
          <MenuSection>
            <Header className={styles.menuHeader}>{t.shell.theme}</Header>
            {(['system', 'light', 'dark'] as const).map((option) => (
              <MenuItem
                key={option}
                id={option}
                textValue={t.shell.themes[option]}
                className={styles.menuItem}
              >
                {THEME_ICONS[option]}
                {t.shell.themes[option]}
                {theme === option ? (
                  <Check size={16} className={styles.menuCheck} aria-label={t.shell.current} />
                ) : null}
              </MenuItem>
            ))}
          </MenuSection>
          <Separator className={styles.menuSeparator} />
          <MenuItem id="sign-out" textValue={t.shell.signOut} className={styles.menuItem}>
            <LogOut size={16} className="flip-rtl" aria-hidden="true" />
            {t.shell.signOut}
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
